import agent from "..//api/agent";
import { RootStore } from "./rootStore";
import { makeAutoObservable, runInAction } from "mobx";
import screenStatusType, {
  IScreenStatus,
  IUserScreens,
  showScreenAsActivated,
  showScreenAsSelected,
} from "../models/screens";
import { toast } from "react-toastify";
import { IScreensSettings } from "../models/toolbar";
import { getToastTranslation } from "../helpers/translationHelper";

export default class ScreensStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  canUseScreens: boolean = false;
  simplifiedMode: boolean = false;
  hideFirstScreenFromToolbar: boolean = false;
  autoOnairOnActivation: boolean = false;
  autoOnairOnSharing: boolean = false;
  isOnair: boolean = false;
  showScreensModal: boolean = false;
  personalScreen: IScreenStatus | null = null;
  connectedScreens: IScreenStatus[] = [];

  get isToShowScreensTab() {
    return this.canUseScreens && !this.simplifiedMode;
  }

  get isToAShowOnairStickyButton() {
    return this.canUseScreens && this.simplifiedMode;
  }

  get isToShowToolbarScreens() {
    return this.selectedScreens.length > 1 || !this.hideFirstScreenFromToolbar;
  }

  get isToShowAutoOnairCheckBox() {
    const { toolbar, isOfflineModeEnabled } = this.rootStore.commonStore;
    return !isOfflineModeEnabled && 
            toolbar && toolbar.canShare &&
            this.canUseScreens && 
            !this.simplifiedMode
  }

  get orderedScreens() {
    return this.connectedScreens.slice().sort(this.compareByOrder);
  }

  get selectedScreens() {
    return this.connectedScreens
      .filter((x) => showScreenAsSelected(x))
      .slice()
      .sort(this.compareByOrder);
  }

  get activatedScreens() {
    return this.connectedScreens
      .filter((x) => showScreenAsActivated(x))
      .slice()
      .sort(this.compareByOrder);
  }

  get hasActivatedScreens() {
    const psActivated =
      this.personalScreen &&
      this.personalScreen.status === screenStatusType.Activated;
    const otherActivated = this.connectedScreens.filter(
      (x) => x.status === screenStatusType.Activated
    );
    return psActivated || otherActivated.length > 0;
  }

  compareByOrder(a: IScreenStatus, b: IScreenStatus) {
    let comparison = 0;
    if (a.order > b.order) comparison = 1;
    else comparison = -1;

    return comparison;
  }

  setScreensSettings = (settings: IScreensSettings | null) => {
    this.canUseScreens = settings ? settings.canUseScreens : false;
    this.simplifiedMode = settings ? settings.simplifiedMode : false
    this.hideFirstScreenFromToolbar = settings ? settings.hideFirstScreen : false;
    
    if (settings && settings.screens) this.setUserScreens(settings.screens);
    
    //check sulle impostazioni degli autoonair deve essere fatto dopo l'impostazione degli schermi in modo che funzioni quando si entra in un meeting
    this.autoOnairOnSharing = settings ? settings.autoOnAirOnSharing : false;
    const autoOnairOnActivationSetting = settings ? settings.autoOnAirOnActivation : false;
    this.setAutoOnairOnActivation(autoOnairOnActivationSetting);
  }

  setAutoOnairOnActivation = (value: boolean) => {
    this.autoOnairOnActivation = value;
    this.isOnair =  this.isOnair ? true : (this.autoOnairOnActivation && this.hasActivatedScreens);
    this.rootStore.tenantStore.shareContentToScreens();
  };

  setAutoOnairOnSharing = (value: boolean) => {
    this.autoOnairOnSharing = value;
  };

  setIsModalOpen = (value: boolean) => {
    this.showScreensModal = value;
  };

  setUserScreens = (userScreens: IUserScreens) => {
    if (userScreens) {
      this.personalScreen = userScreens.personalScreen;
      this.connectedScreens = userScreens.connectedScreens;
    }
  };

  reloadUserScreens = async () => {
    try {
      const dto = await agent.Screen.reloadScreenConnections();
      if (dto) this.setUserScreens(dto);
    } catch (error) {
      console.log(error);
    }
  };

  setOnair = (value: boolean) => {
    this.isOnair = value;
    this.rootStore.tenantStore.shareContentToScreens();
  }

  moveScreenUp = async (id: number) => {
    try {
      await agent.Screen.moveScreenConnectionUp(id);
      runInAction(() => {
        const screen = this.connectedScreens.filter((x) => x.id === id)[0];
        if (screen && screen.order > 1) {
          const currentOrder = screen.order;
          const previousScreen = this.connectedScreens.filter(
            (x) => x.order === currentOrder - 1
          )[0];
          if (previousScreen) {
            screen.order = previousScreen.order;
            previousScreen.order = currentOrder;
          }
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  moveScreenDown = async (id: number) => {
    try {
      await agent.Screen.moveScreenConnectionDown(id);
      runInAction(() => {
        const screen = this.connectedScreens.filter((x) => x.id === id)[0];
        if (screen && screen.order < this.connectedScreens.length) {
          const currentOrder = screen.order;
          const nextScreen = this.connectedScreens.filter(
            (x) => x.order === currentOrder + 1
          )[0];
          if (nextScreen) {
            screen.order = nextScreen.order;
            nextScreen.order = currentOrder;
          }
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  toggleScreenSelection = async (id: number) => {
    try {
      const newStatus = await agent.Screen.changeScreenConnectionSelection(id);
      runInAction(() => {
        const screen = this.connectedScreens.filter((x) => x.id === id)[0];
        if (screen) {
          screen.status = newStatus;
          this.reloadScreenStatus(screen);
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  toggleScreenActivation = async (id: number, removeWidget: boolean) => {
    try {
      const newStatus = await agent.Screen.changeScreenConnectionActivation(id, removeWidget);
      runInAction(() => {
        const screen = this.connectedScreens.filter((x) => x.id === id)[0];
        if (screen) {
          screen.status = newStatus;
          this.reloadScreenStatus(screen);
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  connectToScreen = async (code: string) => {
    try {
      const newScreen = await agent.Screen.connectToScreen(code);
      if (newScreen) {
        let alreadyAdded = this.connectedScreens.filter((x) => x.id === newScreen.id).length > 0
        if (!alreadyAdded) {
          runInAction(() => this.connectedScreens.push(newScreen)); 
          this.reloadScreenStatus(newScreen);
        } else toast.error(getToastTranslation("screens_already_connected_error"));
      } else toast.error(getToastTranslation("screens_not_found_error"));
    } catch (error) {
      console.log(error);
      toast.error(getToastTranslation("screens_generic_error"));
    }
  };

  changeScreenConnectionLabel = async (id: number, label: string) => {
    try {
      agent.Screen.changeScreenConnectionLabel(id, label);
      runInAction(() => {
        const editedScreen = this.connectedScreens.filter(
          (x) => x.id === id
        )[0];
        editedScreen.label = label;
      });
    } catch (error) {
      console.log(error);
    }
  };

  deleteScreenConnection = async (id: number) => {
    try {
      await agent.Screen.deleteScreeenConnection(id);
      runInAction(() => {
        const screenDeleted = this.connectedScreens.filter(
          (x) => x.id === id
        )[0];
        if (screenDeleted) {
          this.connectedScreens = this.connectedScreens.filter(
            (x) => x.id !== screenDeleted.id
          );
          this.connectedScreens
            .filter((x) => x.order > screenDeleted.order)
            .forEach((x) => x.order--);
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  regeneratePersonalScreen = async () => {
    try {
      const newPersonalScreen = await agent.Screen.regeneratePersonalScreen();
      if (newPersonalScreen) {
        this.personalScreen = newPersonalScreen;
        this.reloadScreenStatus(newPersonalScreen);
      }
    } catch (error) {
      console.log(error);
      toast.error(getToastTranslation("screens_personal_regenerate_error"));
    }
  };

  //chiamato quando c'è un cambiamento tra gli schermi agganciati (agganciamento, selezione, attivazione ecc)
  //valuta dunque anche se deve modificare lo stato di onair e condivide il contenuto attuale
  reloadScreenStatus = (screen: IScreenStatus) => {
    if (screen && screen.status === screenStatusType.Activated) {
      this.isOnair = this.autoOnairOnActivation ? true : this.isOnair;
      this.rootStore.tenantStore.shareContentToScreens();
    }
  };

  clearScreenConnectionStatus = async () => {
    try {
       await agent.Screen.clearScreenConnections();
    } catch (error) {
      console.log(error);
    }
  };
}
